/**
 * StepPlan — the last step of the agent-creation flow: pick a plan, finish.
 *
 * The SAME PricingPlans component as the billing page (/billing/:botId) and
 * the in-flow PlanModal: identical cards, Monthly/Yearly glide, hover
 * popovers. If a plan was activated earlier in the flow (knowledge limit →
 * upgrade → Stripe → back), it is already reflected here (current tier is
 * marked; the header says so). Checkout returns to this step, where the shell
 * confirms the new plan. "Finish setup" marks onboarding complete and opens
 * the new agent's Overview.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import PricingPlans from '../../../components/PricingPlans.js';
import { navigateExternal } from '../../../utils/navigation.js';
import { authFetch } from '../../../utils/api';
import { API, planName, startCheckout, fetchPlans } from '../steps.js';
import StepFooter from './StepFooter.js';
import './StepPlan.css';

export default function StepPlan({ agent, botId, goBack, notify, returnPath, registerBeforeLeave, onFinish, planPrices = null, busy = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('month');
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPlans().then((p) => { if (alive) { setPlans(p); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  // Same current-state rules as the billing page / PlanModal.
  const sub = agent?.subscription || null;
  const name = planName(agent);
  const isPaid = name !== 'Free';
  const currentTier = isPaid ? name : null;
  const currentInterval = agent?.subscription_plan?.billing_interval || 'month';
  const isInternalSub = sub?.stripe_subscription_id === 'internal';
  const isWix = !!agent?.wix_product;
  // A Wix app with its own listing prices (flow plan_prices) shows those; other Wix apps keep the internal Wix prices.
  const appPrice = row => planPrices?.[row.name]?.[row.billing_interval === 'year' ? 'yearly' : 'monthly'];
  const displayedPlans = isWix ? plans.map(row => ({...row, monthly_price:appPrice(row) ?? row.wix_monthly_price ?? row.monthly_price})) : plans;
  const onPaidStripe = !isWix && isPaid && !isInternalSub && (!!sub?.stripe_customer_id || !!sub?.stripe_subscription_id);
  const isCancelling = onPaidStripe && agent?.status_display?.className === 'ending';
  const endsText = agent?.status_display?.text || '';

  const choose = useCallback(async (row, key) => {
    setError(''); setPending(key);
    try { await startCheckout({ agent, plan: row, returnPath }); }
    catch (e) { console.error('checkout failed', e); setError(t('billing_checkout_start_error')); setPending(null); }
  }, [agent, returnPath, t]);

  const portal = useCallback(async (key) => {
    if (!sub?.stripe_customer_id && !sub?.stripe_subscription_id) { setError(t('billing_no_stripe_sub')); return; }
    setError(''); setPending(key);
    try {
      const res = await fetch(`${API}create-customer-portal-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_customer_id: sub.stripe_customer_id || null, stripe_subscription_id: sub.stripe_subscription_id || null }),
      });
      const data = await res.json();
      if (data?.url) navigateExternal(data.url); else { setError(t('billing_portal_open_error')); setPending(null); }
    } catch (e) { console.error('portal failed', e); setError(t('billing_portal_open_error')); setPending(null); }
  }, [sub, t]);

  // Finish: mark onboarding complete, clear the resume pointer, open the agent.
  const finish = async () => {
    if (finishing || busy) return;
    // Acquisition onboarding owns its installation progress and portal entry.
    if (onFinish) { await onFinish(); return; }
    setFinishing(true);
    try {
      await authFetch(`${API}onboarding/complete`, { method: 'POST', body: JSON.stringify({ bot_id: botId }) }).catch(() => {});
      await authFetch(`${API}onboarding/progress`, { method: 'PUT', body: JSON.stringify({ step: 0, bot_id: null }) }).catch(() => {});
      registerBeforeLeave?.(null);
      localStorage.setItem('selectedBotId', botId);
      localStorage.setItem('selectedBotIds', JSON.stringify([botId]));
      window.dispatchEvent(new CustomEvent('ub:select-bot', { detail: { botId } }));
      navigate('/overview', { replace: true });
    } catch (e) {
      console.error(e); setFinishing(false); notify(t('ac_plan_finish_failed'), 'error');
    }
  };

  return (
    <>
      <header className="ac-head">
        <h2 className="ac-title">{isPaid ? t('ac_plan_title_paid', { plan: name }) : t('ac_plan_title', { name: agent?.name || '' })}</h2>
        <p className="ac-sub">{isPaid ? t('ac_plan_sub_paid') : t('ac_plan_sub')}</p>
      </header>

      <div className="ac-body">
        {isPaid && (
          <div className="ac-plan-state is-paid">
            <FontAwesomeIcon icon={faCircleCheck} />
            <div>
              <strong>{t('ac_plan_state_active', { plan: name })}{currentInterval === 'year' ? t('billing_yearly_suffix') : ''}</strong>
              <span>{endsText || t('ac_plan_state_active_sub')}</span>
            </div>
          </div>
        )}

        {/* .bbp-page carries the rk-* design tokens the pricing cards read. */}
        <div className="bbp-page ac-plan-body">
          {loading && !plans.length ? (
            <div className="ac-loading"><span className="ac-spinner" /></div>
          ) : (
            <PricingPlans
              plans={displayedPlans}
              creditsLabel={!!planPrices}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycle}
              currentTier={currentTier}
              currentInterval={currentInterval}
              onPaidStripe={onPaidStripe}
              isCancelling={isCancelling}
              endsText={endsText}
              pending={pending}
              busy={pending !== null}
              onChoose={(row, key) => (onPaidStripe ? portal(key) : choose(row, key))}
              onPortal={portal}
            />
          )}
          {error && <div className="ac-error">{error}</div>}
        </div>
      </div>

      <StepFooter onBack={goBack} primaryLabel={t('ac_plan_finish')} onPrimary={finish} busy={finishing || busy}
        hint={isPaid ? null : t('ac_plan_finish_hint')} />
    </>
  );
}
